import {
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
  HttpEvent
} from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { Apiservice } from './apiservice';
import { AuthResponseInterface } from './apiservicemodel';
import { AuthService } from '../auth.service';
import { TokenService } from '../token.service';
import { LoggerService } from '../logger.service';
import { RefreshStateService } from './refresh-state.service';

export function AuthInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const platformId = inject(PLATFORM_ID);

  if (!isPlatformBrowser(platformId)) {
    return next(request);
  }

  const router = inject(Router);
  const tokenService = inject(TokenService);
  const apiService = inject(Apiservice);
  const authService = inject(AuthService);
  const logger = inject(LoggerService);
  const refreshState = inject(RefreshStateService);
  const token = tokenService.getAccessToken();

  if (token) {
    request = request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // Não tentar retry em requests multipart — FormData é um stream não reutilizável
        const isMultipart = request.headers.get('Content-Type')?.includes('multipart') ||
          request.body instanceof FormData;
        if (isMultipart) {
          tokenService.clearTokens();
          router.navigate(['/'], { queryParams: { sessionExpired: 'true' } });
          return throwError(() => error);
        }
        return handle401Error(request, next, router, apiService, authService, tokenService, logger, refreshState);
      }
      return throwError(() => error);
    })
  );
}

function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  router: Router,
  apiService: Apiservice,
  authService: AuthService,
  tokenService: TokenService,
  logger: LoggerService,
  refreshState: RefreshStateService
): Observable<HttpEvent<unknown>> {

  if (!refreshState.isRefreshing) {
    refreshState.isRefreshing = true;
    refreshState.tokenSubject.next(null);

    const refreshToken = tokenService.getRefreshToken();
    const userId = authService.getCurrentUserValue()?.id || tokenService.getUserId();

    if (!refreshToken || !userId) {
      refreshState.isRefreshing = false;
      logger.error('AuthInterceptor - Faltando refresh token ou userId');
      tokenService.clearTokens();
      router.navigate(['/'], { queryParams: { sessionExpired: 'true' } });
      return throwError(() => new Error('Sessão expirada. Por favor, faça login novamente.'));
    }

    return apiService.refreshToken({ userId, refreshToken }).pipe(
      switchMap((response: AuthResponseInterface) => {
        refreshState.isRefreshing = false;
        tokenService.setAccessToken(response.access_token);
        tokenService.setRefreshToken(response.refresh_token);
        refreshState.tokenSubject.next(response.access_token);
        return next(request.clone({ setHeaders: { Authorization: `Bearer ${response.access_token}` } }));
      }),
      catchError((refreshError) => {
        refreshState.isRefreshing = false;
        refreshState.tokenSubject.next(null);
        logger.error('AuthInterceptor - Falha no refresh do token:', refreshError.status);
        tokenService.clearTokens();
        // Redirecionar para home com flag para abrir modal de login
        router.navigate(['/'], { queryParams: { sessionExpired: 'true' } });
        return throwError(() => refreshError);
      })
    );
  }

  return refreshState.tokenSubject.pipe(
    filter(token => token !== null),
    take(1),
    switchMap(token =>
      next(request.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))
    ),
    catchError(() => {
      tokenService.clearTokens();
      router.navigate(['/'], { queryParams: { sessionExpired: 'true' } });
      return throwError(() => new Error('Token refresh failed'));
    })
  );
}

